import { useState, useCallback } from 'react';
import type { AIOperationStatus, AIOperationState } from '../components/shared/AIOperationFeedback';

interface UseAIOperationStateOptions {
  operation: string;
  canCancel?: boolean;
  canRetry?: boolean;
  canContinueInBackground?: boolean;
  estimatedTime?: number;
}

interface UseAIOperationStateReturn {
  state: AIOperationState;
  startOperation: () => void;
  updateProgress: (progress: number, currentStep?: string) => void;
  setCurrentStep: (step: string) => void;
  completeOperation: () => void;
  errorOperation: (error: string) => void;
  resetOperation: () => void;
  setStatus: (status: AIOperationStatus) => void;
}

export function useAIOperationState({
  operation,
  canCancel = true,
  canRetry = true,
  canContinueInBackground = false,
  estimatedTime
}: UseAIOperationStateOptions): UseAIOperationStateReturn {
  const [state, setState] = useState<AIOperationState>({
    status: 'idle',
    canCancel,
    canRetry,
    canContinueInBackground,
    estimatedTime
  });

  const startOperation = useCallback(() => {
    setState(prev => ({
      ...prev,
      status: 'preparing',
      progress: undefined,
      currentStep: undefined,
      error: undefined
    }));
  }, []);

  const updateProgress = useCallback((progress: number, currentStep?: string) => {
    setState(prev => ({
      ...prev,
      progress: Math.max(0, Math.min(100, progress)),
      currentStep: currentStep || prev.currentStep
    }));
  }, []);

  const setCurrentStep = useCallback((currentStep: string) => {
    setState(prev => ({
      ...prev,
      currentStep
    }));
  }, []);

  const setStatus = useCallback((status: AIOperationStatus) => {
    setState(prev => ({
      ...prev,
      status
    }));
  }, []);

  const completeOperation = useCallback(() => {
    setState(prev => ({
      ...prev,
      status: 'complete',
      progress: 100,
      currentStep: undefined,
      error: undefined
    }));
  }, []);

  const errorOperation = useCallback((error: string) => {
    setState(prev => ({
      ...prev,
      status: 'error',
      error,
      currentStep: undefined
    }));
  }, []);

  const resetOperation = useCallback(() => {
    setState({
      status: 'idle',
      canCancel,
      canRetry,
      canContinueInBackground,
      estimatedTime
    });
  }, [canCancel, canRetry, canContinueInBackground, estimatedTime]);

  return {
    state,
    startOperation,
    updateProgress,
    setCurrentStep,
    setStatus,
    completeOperation,
    errorOperation,
    resetOperation
  };
}